// ReinstateBookingModal — staff revive a cancelled booking with an
// optional new schedule (date / timeframe / driver / notes) and choose
// whether to notify the customer.
//
// Mirrors the BookingCreatePage notification section pattern: two
// inline Checkboxes ("Send … email" / "Send … SMS") inside a
// `toggleRow` so the styling is consistent.
//
// Server contract (server/src/modules/scrap/bookings/bookings.types.ts
// reinstateBookingSchema):
//   POST /api/v1/collections/bookings/:enc_id/reinstate
//     body: { collection_date?, timeframe?, driver?, notes?,
//             notify_email, notify_sms }
//   - Server requires booking.status='cancelled' (409 otherwise).
//   - Each schedule field is optional — omitting keeps the existing value.

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { Textarea } from '../../../components/form/Textarea';
import { Select } from '../../../components/form/Select';
import { DatePicker } from '../../../components/form/DatePicker';
import { Checkbox } from '../../../components/form/Checkbox';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { useToast } from '../../../hooks/useToast';
import { useActiveDrivers } from '../../bookings/hooks/useDriversList';
import { useTimeSlots } from '../../bookings/hooks/useReferenceData';
import type {
  BookingSummary,
  ReinstateBookingPayload,
  SlotDayType,
  Timeframe,
} from '../../../types/api';
import styles from './ReinstateBookingModal.module.css';

function slotDayType(dateStr: string): SlotDayType {
  if (!dateStr) return 'weekday';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return 'weekday';
  const day = new Date(
    Number(parts[0]),
    Number(parts[1]) - 1,
    Number(parts[2]),
  ).getDay();
  return day === 0 || day === 6 ? 'weekend' : 'weekday';
}

export interface ReinstateBookingModalProps {
  open: boolean;
  booking: BookingSummary | null;
  onClose: () => void;
  onSuccess: (encId: string) => void;
}

export function ReinstateBookingModal({
  open,
  booking,
  onClose,
  onSuccess,
}: ReinstateBookingModalProps): JSX.Element | null {
  const toast = useToast();
  const drivers = useActiveDrivers();

  const [collectionDate, setCollectionDate] = useState('');
  const [timeframe, setTimeframe] = useState<Timeframe>('AM');
  const [driverId, setDriverId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySms, setNotifySms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const timeSlots = useTimeSlots(slotDayType(collectionDate));

  useEffect(() => {
    if (open && booking !== null) {
      setCollectionDate(booking.date ?? '');
      setTimeframe(booking.timeframe);
      setDriverId(booking.driver_id);
      setNotes(booking.notes ?? '');
      setNotifyEmail(true);
      setNotifySms(false);
      setSubmitting(false);
      setErrorMsg(null);
    }
  }, [open, booking]);

  const driverOptions = useMemo(
    () =>
      [{ value: '', label: 'Unassigned' }].concat(
        (drivers.data ?? []).map((d) => ({
          value: String(d.id),
          label: d.name,
        })),
      ),
    [drivers.data],
  );

  const timeframeOptions = useMemo(
    () =>
      (timeSlots.data ?? []).map((t) => ({ value: t.label, label: t.label })),
    [timeSlots.data],
  );

  const canSubmit = !submitting && booking !== null && collectionDate !== '';

  async function handleSubmit() {
    if (!canSubmit || booking === null) return;
    setSubmitting(true);
    setErrorMsg(null);
    const payload: ReinstateBookingPayload = {
      collection_date: collectionDate,
      timeframe,
      driver: driverId,
      notes: notes.trim().length > 0 ? notes : null,
      notify_email: notifyEmail,
      notify_sms: notifySms,
    };
    try {
      await apiClient.post(
        endpoints.bookings.reinstate(booking.enc_id),
        payload,
      );
      toast.success(`Booking ${booking.reference} reinstated`);
      onSuccess(booking.enc_id);
      onClose();
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not reinstate booking.'
        : 'Could not reinstate booking.';
      setErrorMsg(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title="Reinstate Booking"
      size="md"
      preventClose={submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!canSubmit}
          >
            Reinstate Booking
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <p className={styles.subtitle}>
          Reinstate booking <strong>{booking?.reference ?? '—'}</strong>
          {booking?.name ? (
            <>
              {' '}
              for <strong>{booking.name}</strong>
            </>
          ) : null}
          . You can update the schedule below.
        </p>

        <div className={styles.grid2}>
          <DatePicker
            label="Collection date"
            required
            value={collectionDate}
            onChange={(e) => setCollectionDate(e.target.value)}
          />
          <Select
            label="Time slot"
            options={timeframeOptions}
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value as Timeframe)}
          />
        </div>

        <Select
          label="Driver"
          options={driverOptions}
          value={driverId !== null ? String(driverId) : ''}
          onChange={(e) =>
            setDriverId(e.target.value === '' ? null : Number(e.target.value))
          }
        />

        <Textarea
          label="Additional notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          autoResize
          placeholder="Notes shared with the driver…"
        />

        <div className={styles.notify}>
          <span className={styles.label}>Notify customer</span>
          <div className={styles.toggleRow}>
            <Checkbox
              checked={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.checked)}
              label="Send reinstatement email"
            />
            <Checkbox
              checked={notifySms}
              onChange={(e) => setNotifySms(e.target.checked)}
              label="Send reinstatement SMS"
            />
          </div>
        </div>

        {errorMsg !== null ? (
          <p role="alert" className={styles.error}>
            {errorMsg}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
